import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import MuiTable from "../../../../common-utilities/core/mui-table";

const formatCurrency = (val) =>
  val != null ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "-";

const getItemLabel = (item) =>
  item?.itemName || item?.item?.name || item?.itemCode || item?.item?.itemCode || "Item";

// Old (pre-edit) vs new values for the fields most likely to matter to an approver re-reviewing
// an edited PR -- header totals/justification/vendor plus a per-item quantity/rate/total
// breakdown. `oldPR` is the previous (now-replaced) cycle's full document snapshot
// (Approval.document, captured verbatim at the moment that cycle began); `newPR` is the live PR.
const buildDiffRows = (oldPR, newPR) => {
  const rows = [];
  const pushIfDifferent = (label, oldVal, newVal, formatter = (v) => v ?? "-") => {
    const oldFormatted = formatter(oldVal);
    const newFormatted = formatter(newVal);
    if (oldFormatted !== newFormatted) {
      rows.push({ field: label, oldValue: oldFormatted, newValue: newFormatted });
    }
  };

  pushIfDifferent("Justification", oldPR?.justification, newPR?.justification);
  pushIfDifferent("Preferred Vendor", oldPR?.preferredVendorName, newPR?.preferredVendorName);
  pushIfDifferent("Total Estimated Amount", oldPR?.totalEstimatedGrossAmount, newPR?.totalEstimatedGrossAmount, formatCurrency);

  const oldItemsById = new Map((oldPR?.items || []).map((i) => [String(i._id), i]));
  (newPR?.items || []).forEach((item) => {
    const oldItem = oldItemsById.get(String(item._id));
    if (!oldItem) {
      rows.push({ field: `${getItemLabel(item)} (new item)`, oldValue: "-", newValue: formatCurrency(item.estimatedTotalAmount) });
      return;
    }
    pushIfDifferent(`${getItemLabel(item)} — Qty`, oldItem.quantity, item.quantity);
    pushIfDifferent(`${getItemLabel(item)} — Rate`, oldItem.estimatedRate, item.estimatedRate, formatCurrency);
    pushIfDifferent(`${getItemLabel(item)} — Total`, oldItem.estimatedTotalAmount, item.estimatedTotalAmount, formatCurrency);
  });

  return rows;
};

const DIFF_COLUMNS = [
  { label: "Field", id: "field" },
  { label: "Old Value", id: "oldValue" },
  { label: "New Value", id: "newValue" },
];

// Compact "Changes Made on Edit" indicator shown next to Justification once a PR has been
// edited and resent at least once (pr.previousApprovalIds is non-empty). Diffs the
// immediately-preceding cycle's PR snapshot (Approval.document) against the live PR -- no
// separate diff-storage needed, the snapshot already captures exactly what the PR looked like
// right before this edit. The full field-by-field breakdown only appears in the modal, opened
// via the history icon, rather than inline on the page.
const EditedPRChanges = ({ pr }) => {
  const [diffRows, setDiffRows] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const prId = pr?.id || pr?._id;
    const previousApprovalIds = pr?.previousApprovalIds || [];
    if (!prId || previousApprovalIds.length === 0) return;

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_APPROVALS}`,
      null,
      { documentId: prId, approvalType: "pr_approval" }
    )
      .then((response) => {
        if (!Array.isArray(response)) return;
        const lastPreviousId = previousApprovalIds[previousApprovalIds.length - 1];
        const lastPreviousIdValue = typeof lastPreviousId === "object" ? lastPreviousId.id : lastPreviousId;
        const previousCycle = response.find((a) => String(a.id) === String(lastPreviousIdValue));
        if (previousCycle?.document) {
          setDiffRows(buildDiffRows(previousCycle.document, pr));
        }
      })
      .catch(() => {});
    // Only re-run when the PR identity or its cycle history actually changes -- not on every
    // unrelated pr field update (e.g. approvalId search-param sync).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pr?.id || pr?._id, pr?.previousApprovalIds?.length]);

  if (!pr?.previousApprovalIds?.length) return null;

  return (
    <>
      <div className="info-box">
        <span>Changes Made on Edit</span>
        <span>
          <Tooltip title="View changes made on last edit">
            <IconButton size="small" onClick={() => setOpen(true)}>
              <HistoryIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </span>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Changes Made on Edit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This Purchase Requisition was edited and resent for approval. The previous approval
            cycle was closed and a new cycle started from stage one. Earlier approval history is
            kept and remains visible.
          </Typography>
          {diffRows?.length > 0 ? (
            <MuiTable columnsList={DIFF_COLUMNS} dataList={diffRows} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No field changes detected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditedPRChanges;
